import React, { useState } from "react";
import { useAtom } from "jotai";
import {
  isAdminAtom,
  isElectronAtom,
  listsAtom,
  themeAtom,
} from "../../../-Atoms";
import {
  db,
  addDoc,
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from "../../../firebaseConfig";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useHistory, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { publish } from "../../../GlobalFunctions/customEvent";
import { checkInvoiceStatus } from "../../../GlobalFunctions/checkInvoiceStatus";
import getJobName from "../../../GlobalFunctions/getJobName";
import { addBusinessDays } from "../../../Utils/dayTypeCalculator";

const showDock = () => {
  publish("showDock");
};

const MenuButton = ({
  jobData,
  clientData,
  setEventDataModalOpen,
  currentTab = "all",
  allMultipleDayJobs = [],
  activeMultiDayId = null,
  setActiveMultiDayId = null,
  onOpenHistory = null,
}) => {
  const [isAdmin] = useAtom(isAdminAtom);
  const [isElectron] = useAtom(isElectronAtom);
  const [lists] = useAtom(listsAtom);
  const [theme] = useAtom(themeAtom);
  const [menuAnchorElement, setMenuAnchorElement] = useState(null);
  const [copyCardIdToClipboardOpen, setCopyCardIdToClipboardOpen] =
    useState(false);
  const [copyJobNumberToClipboardOpen, setCopyJobNumberToClipboardOpen] =
    useState(false);
  const [copyJobNameToClipboardOpen, setCopyJobNameToClipboardOpen] =
    useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [parentDeleteWarningOpen, setParentDeleteWarningOpen] = useState(false);
  const [deleteSuccessSnackbarOpen, setDeleteSuccessSnackbarOpen] =
    useState(false);
  const [deletingChildIds, setDeletingChildIds] = useState(new Set());
  const [deletingAllChildren, setDeletingAllChildren] = useState(false);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const showPreProdAdminActions = currentTab === "preProd" && isAdmin;
  const showDeleteCard =
    isElectron && (currentTab === "preProd" || currentTab === "client");
  const showJobActions = currentTab === "job";
  const showHistory =
    (currentTab === "preProd" || currentTab === "job") && !!onOpenHistory;

  const handleMenuClick = (event) => {
    setMenuAnchorElement(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorElement(null);
  };

  const handleCopyCardIdToClipboardClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setCopyCardIdToClipboardOpen(false);
  };

  const handleCopyJobNumberToClipboardClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setCopyJobNumberToClipboardOpen(false);
  };

  const handleCopyJobNameToClipboardClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setCopyJobNameToClipboardOpen(false);
  };

  const handleDeleteDialogOpen = () => {
    handleMenuClose();

    if (jobData.multiDayChildren && jobData.multiDayChildren.length > 0) {
      setParentDeleteWarningOpen(true);
      return;
    }

    setDeleteDialogOpen(true);
  };

  const handleParentDeleteWarningClose = () => {
    setParentDeleteWarningOpen(false);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteSuccessSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setDeleteSuccessSnackbarOpen(false);
  };

  // Check if job is in shooting list (5f081763cd97556d03075c86)
  const isJobInShootingList = () => {
    const shootingList = lists.find((list) => list.id === "5f081763cd97556d03075c86");
    return shootingList && shootingList.items.includes(jobData.id);
  };

  const duplicateJob = async () => {
    if (!isAdmin) return;
    handleMenuClose();
    const duplicatedJob = { ...jobData };
    duplicatedJob.multipleDays = "";
    duplicatedJob.confirmationStatus = "tbc";
    duplicatedJob.dueDate = null;
    duplicatedJob.express = false;
    duplicatedJob.expressDueDate = null;
    duplicatedJob.catering = [];
    duplicatedJob.imagesScheduledToBeShot = "";
    duplicatedJob.imagesProcessed = "";
    duplicatedJob.imagesProcessedNumbers = 0;
    duplicatedJob.retoucher = "";
    duplicatedJob.retouchingTime = "";
    duplicatedJob.models = [];
    duplicatedJob.hmu = [];
    duplicatedJob.eventNotes = "";
    duplicatedJob.shootSpecialRequirements = "";
    duplicatedJob.invoicingSpecialRequirements = "";
    duplicatedJob.retouchingSpecialRequirements = "";
    duplicatedJob.checklists = [
      { name: "Colour Corrections", id: uuidv4(), checklistItems: [] },
      { name: "Colour Changes", id: uuidv4(), checklistItems: [] },
      { name: "Retouching Notes", id: uuidv4(), checklistItems: [] },
    ];

    delete duplicatedJob.id;

    const docRef = await addDoc(collection(db, "JobData"), duplicatedJob);
    await updateDoc(doc(db, "JobData", docRef.id), { id: docRef.id });

    setEventDataModalOpen(false);
    showDock();
    history.push(location.pathname);
  };

  const deleteJob = async () => {
    if (!isAdmin || !jobData.id) return;
    try {
      const jobRef = doc(db, "JobData", jobData.id);
      const jobSnapshot = await getDoc(jobRef);

      if (!jobSnapshot.exists()) {
        console.error("Job does not exist");
        return;
      }

      const jobDataToDelete = jobSnapshot.data();

      // For multi-day jobs, determine which day to show next
      const isMultiDayJob = jobDataToDelete.parentJobId || jobData.multiDayChildren?.length > 0;
      let nextDayToShow = null;

      if (isMultiDayJob && allMultipleDayJobs.length > 1) {
        const currentIndex = allMultipleDayJobs.findIndex(j => j.id === jobData.id);
        if (currentIndex < allMultipleDayJobs.length - 1) {
          nextDayToShow = allMultipleDayJobs[currentIndex + 1].id;
        } else if (currentIndex > 0) {
          nextDayToShow = allMultipleDayJobs[currentIndex - 1].id;
        }
      }

      // Switch to next day before deleting to prevent UI flicker
      if (nextDayToShow && setActiveMultiDayId) {
        setActiveMultiDayId(nextDayToShow);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Update parent if this is a child job
      if (jobDataToDelete.parentJobId) {
        const parentRef = doc(db, "JobData", jobDataToDelete.parentJobId);
        const parentSnapshot = await getDoc(parentRef);

        if (parentSnapshot.exists()) {
          const parentData = parentSnapshot.data();
          const updatedChildren = (parentData.multiDayChildren || []).filter(
            childId => childId !== jobData.id
          );

          if (updatedChildren.length === 0) {
            const jtsMatch = parentData.jobName?.match(/-([A-Z]+)$/);
            const jts = jtsMatch ? jtsMatch[1] : "";

            await updateDoc(parentRef, {
              isMultiDay: false,
              multiDaySuffix: "",
              multipleDays: "",
              multiDayChildren: [],
              multiDayOrder: null,
              parentJobId: null,
              jobName: `${parentData.jobNumber}-${parentData.client}${jts ? `-${jts}` : ""}`,
            });
          } else {
            await updateDoc(parentRef, { multiDayChildren: updatedChildren });

            // Recalculate due dates based on new last day
            const remainingJobs = allMultipleDayJobs.filter(j => j.id !== jobData.id);
            if (remainingJobs.length > 0) {
              const sortedRemaining = remainingJobs.sort((a, b) =>
                (a.multiDayOrder || 1) - (b.multiDayOrder || 1)
              );
              const newLastDay = sortedRemaining[sortedRemaining.length - 1];

              if (newLastDay.bookedStart) {
                const newLastStart = newLastDay.bookedStart?.toDate
                  ? newLastDay.bookedStart.toDate()
                  : new Date(newLastDay.bookedStart);

                const clientTurnaround = parseInt(clientData?.turnAround, 10) || 5;
                const newDueDate = addBusinessDays(newLastStart, clientTurnaround);

                await Promise.all(remainingJobs.map(async (job) => {
                  await updateDoc(doc(db, "JobData", job.id), {
                    dueDate: newDueDate.toISOString(),
                  });
                }));
              }
            }
          }
        }
      }

      await setDoc(doc(db, "JobDataDeleted", jobData.id), {
        ...jobDataToDelete,
        deletedAt: serverTimestamp(),
      });

      await deleteDoc(jobRef);

      setDeleteSuccessSnackbarOpen(true);

      if (!nextDayToShow) {
        setEventDataModalOpen(false);
        showDock();
        history.push(location.pathname);
      }
    } catch (error) {
      console.error("Error deleting job:", error);
    }
  };

  const createSession = () => {
    handleMenuClose();
    window.ipcRenderer?.send("createSession", {
      jobName: getJobName(jobData),
      cardID: jobData.id,
      jobData: jobData,
    });
  };

  const deleteCard = () => {
    handleMenuClose();
    window.ipcRenderer?.send("eval", `deleteCard("${jobData.id}")`);
  };

  const fixNotesShortcut = () => {
    handleMenuClose();
    window.ipcRenderer?.send(
      "eval",
      `if (isCaptureOpen) {
        createSettingsFile("${jobData.id}")
      }`
    );
  };

  const regenerateJobName = () => {
    handleMenuClose();
    if (!jobData.jobTypeFull || !jobData.jobNumber || !jobData.client) {
      console.error("Missing required fields for job name generation");
      return;
    }

    try {
      const jts = jobData.jobTypeFull.match(/^.*\((-.*)\)/)?.[1] || "";
      const jn = `${jobData.jobNumber}-${jobData.client}${
        jobData.multipleDays ? `-${jobData.multipleDays}` : ""
      }${jts}`;

      setDoc(
        doc(db, "JobData", jobData.id),
        { jobName: jn || null },
        { merge: true }
      );
    } catch (error) {
      console.error("Error regenerating job name:", error);
    }
  };

  const handleCheckInvoiceStatus = () => {
    handleMenuClose();
    checkInvoiceStatus(jobData.id, jobData, lists, false);
  };

  const handleDeleteChildDay = async (childId) => {
    if (!isAdmin || deletingChildIds.has(childId)) return;

    setDeletingChildIds(prev => new Set(prev).add(childId));

    try {
      // Get child job data
      const childRef = doc(db, "JobData", childId);
      const childSnapshot = await getDoc(childRef);

      if (!childSnapshot.exists()) {
        console.error("Child job does not exist");
        return;
      }

      const childData = childSnapshot.data();

      // Remove from parent's children array
      const updatedChildren = (jobData.multiDayChildren || []).filter(
        id => id !== childId
      );

      await updateDoc(doc(db, "JobData", jobData.id), {
        multiDayChildren: updatedChildren
      });

      // If no more children, reset parent to single-day
      if (updatedChildren.length === 0) {
        const jtsMatch = jobData.jobName?.match(/-([A-Z]+)$/);
        const jts = jtsMatch ? jtsMatch[1] : "";

        await updateDoc(doc(db, "JobData", jobData.id), {
          isMultiDay: false,
          multiDaySuffix: "",
          multipleDays: "",
          multiDayChildren: [],
          multiDayOrder: null,
          parentJobId: null,
          jobName: `${jobData.jobNumber}-${jobData.client}${jts ? `-${jts}` : ""}`,
        });
      } else {
        // Recalculate due dates for remaining children
        const remainingJobs = allMultipleDayJobs.filter(j => j.id !== childId);
        if (remainingJobs.length > 0) {
          const sortedRemaining = remainingJobs.sort((a, b) =>
            (a.multiDayOrder || 1) - (b.multiDayOrder || 1)
          );
          const newLastDay = sortedRemaining[sortedRemaining.length - 1];

          if (newLastDay.bookedStart) {
            const newLastStart = newLastDay.bookedStart?.toDate
              ? newLastDay.bookedStart.toDate()
              : new Date(newLastDay.bookedStart);

            const clientTurnaround = parseInt(clientData?.turnAround, 10) || 5;
            const newDueDate = addBusinessDays(newLastStart, clientTurnaround);

            const dueDateUpdatePromises = remainingJobs.map(async (job) => {
              await updateDoc(doc(db, "JobData", job.id), {
                dueDate: newDueDate.toISOString(),
              });
            });

            await Promise.all(dueDateUpdatePromises);
          }
        }
      }

      // Archive the child job
      await setDoc(doc(db, "JobDataDeleted", childId), {
        ...childData,
        deletedAt: serverTimestamp(),
      });

      await deleteDoc(childRef);

      // Remove from deleting state
      setDeletingChildIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(childId);
        return newSet;
      });

      // If all children deleted, close modal
      if (updatedChildren.length === 0) {
        setParentDeleteWarningOpen(false);
      }

    } catch (error) {
      console.error("Error deleting child day:", error);
      setDeletingChildIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(childId);
        return newSet;
      });
    }
  };

  const handleDeleteAllChildren = () => {
    if (!isAdmin || deletingAllChildren) return;
    setDeleteAllConfirmOpen(true);
  };

  const confirmDeleteAllChildren = async () => {
    setDeleteAllConfirmOpen(false);
    setDeletingAllChildren(true);

    try {
      const childIds = jobData.multiDayChildren || [];

      // Delete each child
      for (const childId of childIds) {
        const childRef = doc(db, "JobData", childId);
        const childSnapshot = await getDoc(childRef);

        if (childSnapshot.exists()) {
          const childData = childSnapshot.data();

          await setDoc(doc(db, "JobDataDeleted", childId), {
            ...childData,
            deletedAt: serverTimestamp(),
          });

          await deleteDoc(childRef);
        }
      }

      // Archive and delete the parent job as well
      const parentRef = doc(db, "JobData", jobData.id);
      const parentSnapshot = await getDoc(parentRef);

      if (parentSnapshot.exists()) {
        const parentData = parentSnapshot.data();

        await setDoc(doc(db, "JobDataDeleted", jobData.id), {
          ...parentData,
          deletedAt: serverTimestamp(),
        });

        await deleteDoc(parentRef);
      }

      setDeletingAllChildren(false);
      setParentDeleteWarningOpen(false);
      setDeleteSuccessSnackbarOpen(true);

      // Close the modal and return to calendar
      setEventDataModalOpen(false);
      showDock();
      history.push(location.pathname);

    } catch (error) {
      console.error("Error deleting all children and parent:", error);
      setDeletingAllChildren(false);
    }
  };

  const resetStartShooting = async () => {
    handleMenuClose();
    if (!jobData.id) return;

    try {
      const shootingListId = "5f081763cd97556d03075c86";

      // Remove job from shooting list
      const updatedLists = lists.map((list) => {
        if (list.id === shootingListId) {
          return {
            ...list,
            items: list.items.filter((id) => id !== jobData.id),
          };
        }
        return list;
      });

      // Update lists in Firestore
      await updateDoc(doc(db, "AdminCollection", "lists"), {
        lists: updatedLists,
      });

      // Reset shooting status in job data
      await updateDoc(doc(db, "JobData", jobData.id), {
        isInShooting: false,
        isInShootingTime: null,
      });
    } catch (error) {
      console.error("Error resetting start shooting:", error);
    }
  };

  return (
    <>
      <div
        style={{
          position: "absolute",
          top: 12,
          right: 5,
          zIndex: 99999,
          "-webkit-app-region": "no-drag",
          "app-region": "no-drag",
          appRegion: "no-drag",
        }}
      >
        <IconButton onClick={handleMenuClick}>
          <MenuIcon />
        </IconButton>
      </div>
      <Menu
        anchorEl={menuAnchorElement}
        keepMounted
        open={Boolean(menuAnchorElement)}
        onClose={handleMenuClose}
        disableScrollLock={true}
        style={{ marginTop: 10, zIndex: 9999 }} // Ensure menu appears above modal content
      >
        {/* Copy functions - available on all tabs for everyone */}
        <MenuItem
          onClick={() => {
            navigator.clipboard.writeText(jobData.jobNumber);
            setCopyJobNumberToClipboardOpen(true);
            handleMenuClose();
          }}
        >
          Copy Job Number
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            navigator.clipboard.writeText(getJobName(jobData));
            setCopyJobNameToClipboardOpen(true);
            handleMenuClose();
          }}
        >
          Copy Job Name
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            navigator.clipboard.writeText(jobData.id);
            setCopyCardIdToClipboardOpen(true);
            handleMenuClose();
          }}
        >
          Copy Job ID
        </MenuItem>
        <Divider />

        {showHistory && (
          <>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                onOpenHistory();
              }}
            >
              History
            </MenuItem>
            {(showPreProdAdminActions || showDeleteCard || showJobActions) && (
              <Divider />
            )}
          </>
        )}

        {/* PreProd Tab specific functions */}
        {showPreProdAdminActions && (
          <>
            <MenuItem sx={{ minWidth: 180 }} onClick={duplicateJob}>
              Duplicate Job
            </MenuItem>
            <Divider />
            <MenuItem sx={{ minWidth: 180 }} onClick={handleDeleteDialogOpen}>
              Delete Job
            </MenuItem>
            <Divider />
          </>
        )}

        {/* Delete Card - PreProd and Client tabs only (Electron) */}
        {showDeleteCard && (
            <>
              <MenuItem onClick={deleteCard}>Delete Card</MenuItem>
              <Divider />
            </>
          )}

        {/* Job Tab specific functions */}
        {showJobActions && (
          <>
            {isElectron && (
              <>
                <MenuItem onClick={createSession}>Create Session</MenuItem>
                <Divider />
                <MenuItem onClick={fixNotesShortcut}>
                  Fix Notes Shortcut
                </MenuItem>
                <Divider />
              </>
            )}
            {isJobInShootingList() && (
              <>
                <MenuItem onClick={resetStartShooting}>
                  Reset Start Shooting
                </MenuItem>
                <Divider />
              </>
            )}
            <MenuItem onClick={regenerateJobName}>Fix Job Name</MenuItem>
            <Divider />
            <MenuItem onClick={handleCheckInvoiceStatus}>
              Check Invoice Status
            </MenuItem>
            <Divider />
          </>
        )}
      </Menu>
      <Snackbar
        open={copyCardIdToClipboardOpen}
        autoHideDuration={3000}
        onClose={handleCopyCardIdToClipboardClose}
        message="Job ID copied to clipboard"
      />
      <Snackbar
        open={copyJobNumberToClipboardOpen}
        autoHideDuration={3000}
        onClose={handleCopyJobNumberToClipboardClose}
        message="Job Number copied to clipboard"
      />
      <Snackbar
        open={copyJobNameToClipboardOpen}
        autoHideDuration={3000}
        onClose={handleCopyJobNameToClipboardClose}
        message="Job Name copied to clipboard"
      />
      <Snackbar
        open={deleteSuccessSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleDeleteSuccessSnackbarClose}
        message="Job successfully deleted"
      />

      {/* Delete Job Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-job-dialog-title"
        aria-describedby="delete-job-dialog-description"
      >
        <DialogTitle id="delete-job-dialog-title">Delete Job</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-job-dialog-description">
            Are you sure you want to delete this job?
            <br />
            The job won't be permanently deleted it will be moved to an archive
            and can be recovered.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDeleteDialogClose();
              deleteJob();
            }}
            color="error"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Parent Job Delete Warning Dialog - Redesigned */}
      <Dialog
        open={parentDeleteWarningOpen}
        onClose={handleParentDeleteWarningClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: theme === "dark" ? "#1e1e1e" : "#ffffff" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pb: 1,
            color: theme === "dark" ? "#fff" : "#000",
          }}
        >
          <WarningAmberIcon sx={{ color: "#ff9800", fontSize: 32 }} />
          <Box>
            <Typography variant="h6" component="div" fontWeight={600}>
              Delete Multi-Day Job
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              This is Day 1 of a {jobData.multiDayChildren?.length + 1}-day job
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Choose which days to delete:
          </Typography>

          <Stack spacing={2}>
            {allMultipleDayJobs
              .filter(job => jobData.multiDayChildren?.includes(job.id))
              .sort((a, b) => (a.multiDayOrder || 1) - (b.multiDayOrder || 1))
              .map((childJob) => {
                const isDeleting = deletingChildIds.has(childJob.id);
                const shootDate = childJob.bookedStart?.toDate
                  ? childJob.bookedStart.toDate()
                  : new Date(childJob.bookedStart);

                return (
                  <Paper
                    key={childJob.id}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      border: `1px solid ${
                        theme === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"
                      }`,
                      borderRadius: 2,
                      backgroundColor:
                        theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: "1.125rem",
                          fontWeight: 700,
                          color: theme === "dark" ? "#64b5f6" : "#1976d2",
                        }}
                      >
                        {childJob.multiDaySuffix || childJob.multipleDays || "Day"}
                      </Typography>

                      <Button
                        onClick={() => handleDeleteChildDay(childJob.id)}
                        disabled={isDeleting}
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={
                          isDeleting ? (
                            <CircularProgress size={16} color="error" />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 1.5,
                          minWidth: 90,
                        }}
                      >
                        {isDeleting ? "..." : "Delete"}
                      </Button>
                    </Box>

                    <Typography
                      variant="body1"
                      sx={{
                        color: theme === "dark" ? "#fff" : "#000",
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      {childJob.jobName}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CalendarTodayIcon
                        sx={{
                          fontSize: 16,
                          color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                        }}
                      >
                        {shootDate.toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button
            onClick={handleParentDeleteWarningClose}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllChildren}
            disabled={deletingAllChildren}
            variant="contained"
            color="error"
            startIcon={
              deletingAllChildren ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {deletingAllChildren ? "Deleting..." : "Delete Entire Job"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={deleteAllConfirmOpen}
        onClose={() => setDeleteAllConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: theme === "dark" ? "#1e1e1e" : "#ffffff",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pb: 1,
            color: theme === "dark" ? "#fff" : "#000",
          }}
        >
          <WarningAmberIcon sx={{ color: "#d32f2f", fontSize: 32 }} />
          <Typography variant="h6" component="div" fontWeight={600}>
            Delete Entire Job?
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            This will delete all {jobData.multiDayChildren?.length + 1} days of this job.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Jobs will be archived and can be recovered.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setDeleteAllConfirmOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteAllChildren}
            variant="contained"
            color="error"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Delete Entire Job
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MenuButton;
